import {LineChart, Line, XAxis, CartesianGrid, Tooltip, ResponsiveContainer, Customized} from "recharts";
import moment from "moment";
import React, {useEffect, useMemo, useRef, useState} from "react";
import {LineStartLabels} from "../components/LineStartLabels";
import {LineEndValues} from "../components/LineEndValues";
import dayjs from "dayjs";
import { AppCheckbox, CheckboxHolder } from "../components/form";

const CHART_ROW_META_KEYS = new Set(["date", "dateMs"]);
const rowSeriesKeys = (row) => Object.keys(row).filter((k) => !CHART_ROW_META_KEYS.has(k));

export const ChartWarehouse = () => {
    const [snapshots, setSnapshots] = useState([]);
    const [chartData, setChartData] = useState([]);
    const [stableProducts, setStableProducts] = useState([]);
    const [changingProducts, setChangingProducts] = useState([]);
    const [inputValue, setInputValue] = useState("");
    const [trackedProducts, setTrackedProducts] = useState([]);
    const topSalesRef = useRef([]); // повний рейтинг
    /** Після першого розрахунку серій з chartData не перезаписувати вибір застарілим відповіддю chart-watchlist */
    const chartSelectionFromDataRef = useRef(false);
    const [topOffset, setTopOffset] = useState(0);
    const [chartReady, setChartReady] = useState(false);
    const [viewMode, setViewMode] = useState("product");
    const [summaries, setSummaries] = useState([]);
    const [stocksRows, setStocksRows] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [sortConfig, setSortConfig] = useState({
        key: null,
        direction: "asc"
    });
    const [showMinus, setShowMinus] = useState(true);
    const [showSoldOut, setShowSoldOut] = useState(true);
    const [hideSlow, setHideSlow] = useState(false);




    const PERIODS = {
        currentMonth: { label: "Поточний місяць", type: "currentMonth" },
        last14: { label: "14 днів", days: 14 },
        last7: { label: "7 днів", days: 7 },
        prevMonth: { label: "Попередній місяць", type: "prevMonth" },
    };

    const PERIOD_LABELS = {
        currentMonth: "поточному місяці",
        prevMonth: "попередньому місяці",
        last7: "останні 7 днів",
        last14: "останні 14 днів",
    };

    const [periodKey, setPeriodKey] = useState("last7");


    /** Точки графіка за календарним періодом (не «останні N знімків»). */
    const getPeriodSlice = (data, periodKey) => {
        if (!data.length) return [];

        const { start, end } = getPeriodRange(periodKey);
        const startMs = start.valueOf();
        const endMs = end.valueOf();

        return data.filter((d) => {
            const ms = d.dateMs;
            if (ms == null || Number.isNaN(ms)) return false;
            return ms >= startMs && ms <= endMs;
        });
    };

    const getPeriodRange = (periodKey) => {
        const now = dayjs();

        switch (periodKey) {

            case "last7":
                return {
                    start: now.subtract(6, "day").startOf("day"),
                    end: now.endOf("day"),
                    days: 7
                };

            case "last14":
                return {
                    start: now.subtract(13, "day").startOf("day"),
                    end: now.endOf("day"),
                    days: 14
                };

            case "currentMonth": {
                const start = now.startOf("month");
                const daysPassed = now.diff(start, "day") + 1;

                return {
                    start,
                    end: now.endOf("day"),
                    days: daysPassed   // 🔥 лише фактично минулі дні
                };
            }

            case "prevMonth": {
                const start = now.subtract(1, "month").startOf("month");
                const end = start.endOf("month");

                const daysInMonth = end.diff(start, "day") + 1;

                return {
                    start,
                    end,
                    days: daysInMonth
                };
            }

            default:
                return {
                    start: now.subtract(29, "day").startOf("day"),
                    end: now.endOf("day"),
                    days: 30
                };
        }
    };


    useEffect(() => {
        fetch("/api/chart-watchlist?page=warehouse_chart")
            .then(r => r.json())
            .then(data => {
                if (chartSelectionFromDataRef.current) return;
                setTrackedProducts(data.map(i => i.product_name));
            })
            .catch(() => {});
    }, []);

    useEffect(() => {
        const loadSummary = async () => {
            const { start, end } = getPeriodRange(periodKey);

            const startStr = start.format("YYYY-MM-DD");
            const endStr = end.format("YYYY-MM-DD");

            try {
                const res = await fetch(`/api/summary?start=${startStr}&end=${endStr}`);
                const data = await res.json();
                setSummaries(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("summary load error", e);
                setSummaries([]);
            }
        };

        loadSummary();
    }, [periodKey]);

    const salesMap = useMemo(() => {
        const map = new Map();

        for (const day of summaries) {
            for (const order of day.orders || []) {
                for (const product of order.products || []) {

                    if (!product.sku) continue;

                    const qty = Number(product.quantity || 0);
                    map.set(product.sku, (map.get(product.sku) || 0) + qty);
                }
            }
        }

        return map;
    }, [summaries]);


    const stockMap = useMemo(() => {
        const map = new Map();

        for (const row of stocksRows) {
            if (!row.sku) continue;

            const getProp = (name) =>
                row.properties?.find(p => p.name === name)?.value || "";

            // 🔥 считаем доступное количество по складам
            const totalAvailable = (row.warehouse || []).reduce((sum, wh) => {
                return sum + ((wh.quantity || 0) - (wh.reserve || 0));
            }, 0);

            map.set(row.sku, {
                id: row.id,
                productId: row.product_id,
                name: row.name,
                sku: row.sku,
                stock: totalAvailable,  // ← ВАЖНО
                color: getProp("Колір"),
                size: getProp("Розмір"),
                type: getProp("Тип")
            });
        }

        return map;
    }, [stocksRows]);



    const finishingRows = useMemo(() => {

        const { days } = getPeriodRange(periodKey);
        const rows = [];

        for (const [sku, item] of stockMap.entries()) {

            const sold = salesMap.get(sku) || 0;

            if (sold === 0) continue;

            const speed = sold / days;

            let daysLeft = null;

            if (item.stock === 0) {
                daysLeft = 0;
            } else if (speed < 0.05) {
                daysLeft = null;
            } else {
                daysLeft = item.stock / speed;
            }

            rows.push({
                id: item.id,
                productId: item.productId,
                name: item.name,
                sku: item.sku,
                color: item.color,
                size: item.size,
                type: item.type,
                stock: item.stock,
                sold,
                speed,
                daysLeft
            });
        }

        rows.sort((a, b) => {
            const da = a.daysLeft ?? Infinity;
            const db = b.daysLeft ?? Infinity;
            return da - db;
        });

        return rows;

    }, [stockMap, salesMap, periodKey]);

    const filteredRows = useMemo(() => {

        if (!searchTerm.trim()) return finishingRows;

        const term = searchTerm.toLowerCase();

        return finishingRows.filter(row =>
            row.name?.toLowerCase().includes(term) ||
            row.sku?.toLowerCase().includes(term) ||
            row.color?.toLowerCase().includes(term) ||
            row.size?.toLowerCase().includes(term) ||
            row.type?.toLowerCase().includes(term) ||
            String(row.id).includes(term) ||
            String(row.productId).includes(term)
        );

    }, [finishingRows, searchTerm]);

    



    const allProducts = chartData.length ? rowSeriesKeys(chartData[0]) : [];

    const addProduct = async () => {
        if (!inputValue || trackedProducts.includes(inputValue)) return;

        await fetch("/api/chart-watchlist", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                page: "warehouse_chart",
                product_name: inputValue
            })
        });

        setTrackedProducts(prev => [...prev, inputValue]);
        setInputValue("");
    };

    const removeProduct = async product => {
        await fetch("/api/chart-watchlist", {
            method: "DELETE",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                page: "warehouse_chart",
                product_name: product
            })
        });

        setTrackedProducts(prev => prev.filter(p => p !== product));
    };

    const hasSalesInPeriod = (productName, data, periodKey) => {
        const periodData = getPeriodSlice(data, periodKey);
        if (periodData.length < 2) return false;

        for (let i = 1; i < periodData.length; i++) {
            const prev = periodData[i - 1][productName] ?? 0;
            const curr = periodData[i][productName] ?? 0;
            if (curr < prev) return true;
        }
        return false;
    };

    const sortedRows = useMemo(() => {
        if (!sortConfig.key) return filteredRows;

        const sorted = [...filteredRows].sort((a, b) => {
            const valA = a[sortConfig.key];
            const valB = b[sortConfig.key];

            if (valA === undefined || valA === null) return 1;
            if (valB === undefined || valB === null) return -1;

            if (typeof valA === "number" && typeof valB === "number") {
                return sortConfig.direction === "asc"
                    ? valA - valB
                    : valB - valA;
            }

            return sortConfig.direction === "asc"
                ? String(valA).localeCompare(String(valB), "uk")
                : String(valB).localeCompare(String(valA), "uk");
        });

        return sorted;
    }, [filteredRows, sortConfig]);


    const finalRows = useMemo(() => {
        return sortedRows.filter(row => {

            // SOLD OUT
            if (!showSoldOut && row.stock === 0) {
                return false;
            }

            // Продані в мінус (stock < 0)
            if (!showMinus && row.stock < 0) {
                return false;
            }

            // Повільні
            if (!hideSlow) {
                if (row.speed < 2 && row.daysLeft !== null && row.daysLeft > 10) {
                    return false;
                }
            }

            return true;
        });
    }, [sortedRows, showMinus, showSoldOut, hideSlow]);



    useEffect(() => {
        const fetchSnapshots = async () => {
            try {
                const res = await fetch("/api/warehouse-snapshots");
                const data = await res.json();
                setSnapshots(data);
            } catch (error) {
                console.error("Помилка складу:", error);
            }
        };

        fetchSnapshots();
    }, []);
    useEffect(() => {
        const loadStocks = async () => {
            try {
                const res = await fetch("/api/stocks", {
                    credentials: "include"
                });
                const data = await res.json();
                setStocksRows(Array.isArray(data) ? data : []);
            } catch (e) {
                console.error("stocks load error", e);
                setStocksRows([]);
            }
        };

        loadStocks();
    }, []);




    useEffect(() => {
        if (!Array.isArray(snapshots) || snapshots.length === 0) return;

        const formattedData = snapshots.map(({ date, products }) => {
            const m = moment(date);
            const entry = {
                date: m.format("DD.MM HH:mm"),
                dateMs: m.valueOf(),
            };

            products.forEach(({ product_name, sku, quantity }) => {

                if (viewMode === "product") {
                    entry[product_name] = (entry[product_name] || 0) + Number(quantity || 0);
                } else {
                    entry[sku] = (entry[sku] || 0) + Number(quantity || 0);
                }

            });

            return entry;
        });

        setChartData(formattedData.reverse());
    }, [snapshots, viewMode]);


    useEffect(() => {
        if (!Array.isArray(chartData) || chartData.length === 0) return;

        const allProducts = rowSeriesKeys(chartData[0]);

        const hasChangesInPeriod = (productName, data, periodKey) => {
            const periodData = getPeriodSlice(data, periodKey);
            if (periodData.length < 2) return true;

            const values = periodData.map(d => d[productName] || 0);
            return new Set(values).size > 1;
        };


        const changing = allProducts.filter(product =>
            hasChangesInPeriod(product, chartData, periodKey) &&
            hasSalesInPeriod(product, chartData, periodKey)
        );


        const stable = allProducts.filter(product => !changing.includes(product));

        setChangingProducts(changing);
        setStableProducts(stable);
    }, [chartData, periodKey]);

    const buildTopSalesRanking = (data, periodKey) => {
        if (data.length < 2) return [];

        const periodData = getPeriodSlice(data, periodKey);
        if (periodData.length < 2) return [];

        const deltas = {};

        for (let i = 1; i < periodData.length; i++) {
            const prev = periodData[i - 1];
            const curr = periodData[i];

            Object.keys(curr).forEach(key => {
                if (key === "date" || key === "dateMs") return;

                const diff = (prev[key] ?? 0) - (curr[key] ?? 0);
                if (diff > 0) {
                    deltas[key] = (deltas[key] || 0) + diff;
                }
            });
        }

        return Object.entries(deltas)
            .sort((a, b) => b[1] - a[1])
            .map(([name]) => name);
    };


    useEffect(() => {
        if (!chartData.length) return;

        setChartReady(false);
        chartSelectionFromDataRef.current = true;

        const ranking = buildTopSalesRanking(chartData, periodKey);

        topSalesRef.current = ranking;
        setTopOffset(0);

        const firstTop5 = ranking.slice(0, 5);

        setTrackedProducts(firstTop5);

        // 💾 зберігаємо в БД
        Promise.all(
            firstTop5.map(product =>
                fetch("/api/chart-watchlist", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        page: "warehouse_chart",
                        product_name: product
                    })
                })
            )
        ).finally(() => {
            setChartReady(true);
        });
    }, [chartData, periodKey, viewMode]);



    if (!Array.isArray(chartData) || chartData.length === 0) {
        return <p>Завантаження даних…</p>;
    }



    const keys = trackedProducts.filter(p =>
        chartData.some(d => d[p] !== undefined)
    );

    const showTopBatch = async (newOffset) => {
        const start = Math.max(0, newOffset);
        const end = start + 5;

        const batch = topSalesRef.current.slice(start, end);
        if (!batch.length) return;

        // ❌ видалити поточні товари з БД
        await Promise.all(
            trackedProducts.map(product =>
                fetch("/api/chart-watchlist", {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        page: "warehouse_chart",
                        product_name: product
                    })
                })
            )
        );

        // ✅ зберегти нову п’ятірку
        await Promise.all(
            batch.map(product =>
                fetch("/api/chart-watchlist", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        page: "warehouse_chart",
                        product_name: product
                    })
                })
            )
        );

        setTrackedProducts(batch);
        setTopOffset(start);
    };

    const periodData = getPeriodSlice(chartData, periodKey);

    const handleSort = (key) => {
        setSortConfig(prev => {
            if (prev.key === key) {
                return {
                    key,
                    direction: prev.direction === "asc" ? "desc" : "asc"
                };
            }
            return { key, direction: "asc" };
        });
    };

    const getArrow = (key) => {
        if (sortConfig.key !== key) return "";
        return sortConfig.direction === "asc" ? " ↑" : " ↓";
    };

    const downloadWarehouseExport = async (groupBy) => {
        const q = groupBy === "product" ? "product" : "variant";
        try {
            const res = await fetch(
                `/api/warehouse-snapshots/export?groupBy=${q}&format=csv`,
                { credentials: "include" }
            );
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                alert(err.error || `Помилка ${res.status}`);
                return;
            }
            const blob = await res.blob();
            const dispo = res.headers.get("Content-Disposition");
            const m = dispo && /filename="([^"]+)"/.exec(dispo);
            const filename = m ? m[1] : `warehouse-snapshot_${q}.csv`;
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            a.click();
            URL.revokeObjectURL(url);
        } catch (e) {
            console.error(e);
            alert("Не вдалося завантажити файл");
        }
    };

    return (
       <section className="pt-5">
           <div className="container d-flex justify-content-between">
               <h2>Графік наявності товарів</h2>
               <div style={{ marginBottom: 12 }}>
                   {Object.entries(PERIODS).map(([key, p]) => (
                       <button
                           key={key}
                           onClick={() => setPeriodKey(key)}
                           style={{
                               marginRight: 6,
                               padding: "4px 10px",
                               borderRadius: 6,
                               border: "1px solid #ccc",
                               background: periodKey === key ? "#333" : "#f5f5f5",
                               color: periodKey === key ? "#fff" : "#000"
                           }}
                       >
                           {p.label}
                       </button>
                   ))}
               </div>
           </div>
                   <div className="container">
                       <div style={{ marginBottom: 12 }}>
                           <button
                               onClick={() => setViewMode("product")}
                               style={{
                                   marginRight: 6,
                                   background: viewMode === "product" ? "#333" : "#eee",
                                   color: viewMode === "product" ? "#fff" : "#000"
                               }}
                           >
                               По товару
                           </button>

                           <button
                               onClick={() => setViewMode("sku")}
                               style={{
                                   background: viewMode === "sku" ? "#333" : "#eee",
                                   color: viewMode === "sku" ? "#fff" : "#000"
                               }}
                           >
                               По SKU
                           </button>

                           <span style={{ marginLeft: 16, color: "#666" }}>Вигрузка останнього знімка:</span>
                           <button
                               type="button"
                               onClick={() => downloadWarehouseExport("product")}
                               style={{
                                   marginLeft: 8,
                                   padding: "4px 10px",
                                   borderRadius: 6,
                                   border: "1px solid #ccc",
                                   background: "#f8f8f8",
                               }}
                           >
                               CSV — по товару
                           </button>
                           <button
                               type="button"
                               onClick={() => downloadWarehouseExport("variant")}
                               style={{
                                   marginLeft: 6,
                                   padding: "4px 10px",
                                   borderRadius: 6,
                                   border: "1px solid #ccc",
                                   background: "#f8f8f8",
                               }}
                           >
                               CSV — по варіанту (SKU)
                           </button>
                       </div>
                       <div style={{ marginBottom: 12 }}>

                       </div>
                       {chartData.length > 0 && periodData.length === 0 && (
                           <p style={{ marginBottom: 10 }}>
                               За {PERIOD_LABELS[periodKey]} у даних немає жодного знімка з цією датою:
                               останні знімки в базі старіші за цей інтервал. Оберіть інший період або
                               перевірте, що знімки складу регулярно зберігаються.
                           </p>
                       )}

                       {changingProducts.length === 0 && periodData.length > 0 && (
                           <p style={{ marginBottom: 10 }}>
                               Руху товару не було за {PERIOD_LABELS[periodKey]}.
                           </p>
                       )}


                       <ResponsiveContainer width="100%" height={500}>
                           <LineChart
                               key={viewMode}
                               data={periodData}
                               margin={{ top: 20, right: 40, left: 180, bottom: 10 }}
                           >
                               <CartesianGrid
                                   strokeDasharray="3 3"
                                   stroke="rgba(255,255,255,0.15)"
                               />

                               <XAxis
                                   dataKey="date"
                                   tickFormatter={(v) => moment(v, "DD.MM HH:mm").format("DD.MM")}
                               />
                               <Tooltip />

                               {chartReady && keys.map((product_name, index) => (
                                   <Line
                                       key={`${viewMode}-${product_name}`}
                                       type="monotone"
                                       dataKey={product_name}
                                       stroke={getColor(index)}
                                       strokeWidth={2}
                                       dot={false}
                                   />
                               ))}

                               {chartReady && (
                                   <Customized
                                       component={({ formattedGraphicalItems }) => (
                                           <>
                                               <LineStartLabels lines={formattedGraphicalItems} />
                                               <LineEndValues lines={formattedGraphicalItems} /></>
                                       )}
                                   />
                               )}

                           </LineChart>
                       </ResponsiveContainer>
                   </div>
                   <div className="container">
                       <div style={{ marginBottom: 20 }}>
                           <input
                               list="products"
                               value={inputValue}
                               onChange={e => setInputValue(e.target.value)}
                               placeholder="Додати товар"
                           />

                           <datalist id="products">
                               {allProducts.map(p => (
                                   <option key={p} value={p} />
                               ))}
                           </datalist>

                           <button onClick={addProduct} style={{ marginLeft: 8 }}>
                               Додати
                           </button>
                           {topOffset > 0 && (
                               <button
                                   onClick={() => showTopBatch(topOffset - 5)}
                                   style={{
                                       marginLeft: 10,
                                       background: "#666",
                                       color: "#fff",
                                       borderRadius: 6,
                                       padding: "4px 10px"
                                   }}
                               >
                                   ⬅ Назад ({Math.max(1, topOffset - 4)}–{topOffset})
                               </button>
                           )}
                           {topOffset + 5 < topSalesRef.current.length && (
                               <button
                                   onClick={() => showTopBatch(topOffset + 5)}
                                   style={{
                                       marginLeft: 10,
                                       background: "#ff7300",
                                       color: "#fff",
                                       borderRadius: 6,
                                       padding: "4px 10px"
                                   }}
                               >
                                   ➡ Далі ({topOffset + 6}–{topOffset + 10})
                               </button>
                           )}
                       </div>
                       <div style={{ marginBottom: 20 }}>
                           {trackedProducts.map(p => (
                               <span key={p} style={{ marginRight: 10 }}>
      {p}
                                   <button onClick={() => removeProduct(p)} style={{ marginLeft: 4 }}>
        ✕
      </button>
    </span>
                           ))}
                       </div>
                       <div style={{ marginTop: 40, overflowX: "auto" }}>
                           <h3>Швидкість продажів товарів</h3>
                           <div style={{ marginBottom: 15 }}>
                           <CheckboxHolder
                               className="mb-0"
                               formClassName="d-flex flex-wrap gap-3 mb-0"
                           >
                               <AppCheckbox
                                   id="cw-show-minus"
                                   label="Продані в мінус"
                                   checked={showMinus}
                                   onChange={() => setShowMinus((prev) => !prev)}
                               />
                               <AppCheckbox
                                   id="cw-show-sold-out"
                                   label="SOLD OUT"
                                   checked={showSoldOut}
                                   onChange={() => setShowSoldOut((prev) => !prev)}
                               />
                               <AppCheckbox
                                   id="cw-hide-slow"
                                   label="Повільні"
                                   checked={hideSlow}
                                   onChange={() => setHideSlow((prev) => !prev)}
                               />
                           </CheckboxHolder>
                           </div>

                           <div style={{ marginBottom: 15 }}>
                               <input
                                   type="text"
                                   placeholder="Пошук (назва, SKU, колір, розмір, тип...)"
                                   value={searchTerm}
                                   onChange={(e) => setSearchTerm(e.target.value)}
                                   style={{
                                       padding: "16px 10px",
                                       width: "100%",
                                   }}
                               />
                           </div>
                           <div style={{ overflowX: "auto", marginBottom: 280 }}>
                           <table className="table-stocks-left table-stocks resp-tab">
                               <thead>
                               <tr>
                                   <th onClick={() => handleSort("id")}>ID {getArrow("id")}</th>
                                   <th onClick={() => handleSort("name")}>Назва  {getArrow("name")}</th>
                                   <th onClick={() => handleSort("sku")}>SKU  {getArrow("sku")}</th>
                                   <th onClick={() => handleSort("color")}>Колір  {getArrow("color")}</th>
                                   <th onClick={() => handleSort("size")}>Розмір  {getArrow("size")}</th>
                                   <th onClick={() => handleSort("type")}>Тип  {getArrow("type")}</th>
                                   <th onClick={() => handleSort("stock")}>Залишок  {getArrow("stock")}</th>
                                   <th onClick={() => handleSort("sold")}>Продано  {getArrow("sold")}</th>
                                   <th onClick={() => handleSort("speed")}>Швидкість  {getArrow("speed")}</th>
                                   <th onClick={() => handleSort("daysLeft")}>Днів залишилось  {getArrow("daysLeft")}</th>

                               </tr>
                               </thead>
                               <tbody>
                               {finalRows.map((row) => (
                                   <tr key={row.sku}>
                                       <td>{row.id}</td>
                                       <td>{row.name}</td>
                                       <td>{row.sku}</td>
                                       <td>{row.color || "—"}</td>
                                       <td>{row.size || "—"}</td>
                                       <td>{row.type || "—"}</td>
                                       <td>{row.stock}</td>
                                       <td>{row.sold}</td>
                                       <td>{row.speed.toFixed(2)}</td>
                                       <td>
                                           {row.stock === 0
                                               ? "SOLD OUT"
                                               : row.daysLeft === null
                                                   ? "—"
                                                   : Math.floor(row.daysLeft)}
                                       </td>
                                   </tr>
                               ))}
                               </tbody>
                           </table>
                       </div>
                       </div>

                       {/* Список стабільних товарів */}
                       {/*{stableProducts.length > 0 && (*/}
                       {/*    <div>*/}
                       {/*        <h3>Товари без змін за останні 30 днів:</h3>*/}
                       {/*        <ol>*/}
                       {/*            {stableProducts.map((product, index) => {*/}
                       {/*                const latestQuantity = chartData[chartData.length - 1]?.[product] || 0;*/}
                       {/*                return <li key={index}>{index + 1}. {product} - {latestQuantity}</li>;*/}
                       {/*            })}*/}
                       {/*        </ol>*/}
                       {/*    </div>*/}
                       {/*)}*/}


                   </div>
       </section>

    );
};

const getColor = index => {
    const colors = ["#8884d8", "#82ca9d", "#ff7300", "#ff0000", "#00c0ff", "#ff00c0", "#c0ff00"];
    return colors[index % colors.length];
};
